import { ForecastAlert } from '@/components/forecast/ForecastAlert';
import SearchButton from '@/components/forecast/SearchButton';
import Dropdown from '@/components/invoice/Dropdown';
import IndividualResourceInvoiceTable from '@/components/invoice/IndividualResourceInvoiceTable';
import SetResourceInvoiceTable from '@/components/invoice/SetResourceInvoiceTable';
import SideLayout from '@/layouts/SideLayout';
import { get } from '@/utils/fetch';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

const InvoicePage = () => {
  const [invoiceYearMonthRange, setInvoiceYearMonthRange] = useState<{
    [key: string]: Array<string>;
  }>();
  const [invoiceYear, setInvoiceYear] = useState<string>();
  const [invoiceMonth, setInvoiceMonth] = useState<string>();
  const fetchRangeCalled = useRef(false);
  const [showFetchInoviceAlert, setShowFetchInvoiceAlert] = useState(false);

  useEffect(() => {
    if (fetchRangeCalled.current) return;
    fetchRangeCalled.current = true;

    // fetch year month range when mount
    const fetchRange = async () => {
      const response = await get<{ data: { [key: string]: Array<string> } }>(
        '/invoice/range'
      );
      if (response.status === 'success' && response.data) {
        setInvoiceYearMonthRange(response.data);

        const [firstYear, firstMonths] =
          Object.entries(response.data)?.[0] || [];
        const firstMonth = firstMonths?.[0];

        setInvoiceYear(firstYear);
        setInvoiceMonth(firstMonth);
      } else if (response?.status === 'error') {
        setInvoiceYearMonthRange({});
        setInvoiceYear(undefined);
        setInvoiceMonth(undefined);
      }
    };
    fetchRange();
  }, []);

  const [individualResourceInvoiceData, setIndividualResourceData] =
    useState<Array<IndividualResourceInvoiceData>>();
  const [setResourceInvoiceData, setSetResourceInvoiceData] =
    useState<Array<SetResourceInvoiceData>>();

  const debouncedOnclickFetchInvoiceDataButton = debounce(async () => {
    setShowFetchInvoiceAlert(false);
    const response = await get<{ data: Array<invoiceData> }>('/invoice/', {
      params: {
        yearMonth: `${invoiceYear}-${invoiceMonth}`,
      },
    });
    if (response.status === 'success' && response.data) {
      const individualResourceData = response.data
        .filter((item) => item.cuidDerResType === 'individual')
        .map((item) => {
          return {
            siteName: item.siteName,
            siteCapacity: item.capacityMw,
            siteGenerationMwh: (
              Math.round(item.transMeterMwhSite * 100) / 100
            ).toLocaleString(), // round to 2 decimal
            siteIncentive: Math.round(item.incentive).toLocaleString(),
          };
        });
      const setResourceData = response.data
        .filter((item) => item.cuidDerResType === 'set')
        .map((item) => {
          return {
            cuidName: item.cuidName,
            siteName: item.siteName,
            capacity: item.capacityMw,
            generationMwh: (
              Math.round(item.transMeterMwhCuid * 100) / 100
            ).toLocaleString(),
            generationRatio: (
              Math.round(item.transMeterRatio * 100) / 100
            ).toLocaleString(),
            settlementCuid: item.settlementCuid.toLocaleString(),
            distributionRatio: item.distributionRatio,
            siteIncentive: Math.round(item.incentive).toLocaleString(),
            invoiceStatus: item.invoiceStatus,
          };
        });
      setIndividualResourceData(individualResourceData);
      setSetResourceInvoiceData(setResourceData);
    } else if (response?.status === 'error') {
      setShowFetchInvoiceAlert(true);
      setIndividualResourceData([]);
      setSetResourceInvoiceData([]);
    }
  }, 1000);

  return (
    <SideLayout>
      <div className="flex min-h-screen min-w-[1200px] flex-col whitespace-nowrap bg-light-2 px-[50px] py-[50px] [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-[1100px]">
        <header>
          <p className="pop-semibold-5 leading-none">예측제도 정산</p>
        </header>
        <section className="mt-[30px] h-[850px] rounded-[10px] bg-white px-[30px] py-[40px]">
          <div className="noto-15-medium leading-none">정산금 월별 조회</div>
          <div className="mt-[10px] flex items-center">
            <Dropdown
              items={Object.keys(invoiceYearMonthRange || {})}
              selectedItem={invoiceYear}
              className="w-[120px] text-dark-3 [&>div:first-child>button]:noto-medium-4 [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[40px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:pb-[13px] [&>div:first-child>button]:pl-[21px] [&>div:first-child>button]:pr-[15px] [&>div:first-child>button]:pt-[15px] [&>div:first-child>button]:ring-2 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&[data-headlessui-state=open]>div:first-child>button]:bg-light-2 [&[data-headlessui-state=open]>div:first-child>button]:ring-teal-dark"
              onSelected={(selectedItem) => setInvoiceYear(selectedItem)}
            ></Dropdown>
            <Dropdown
              items={
                invoiceYear && invoiceYearMonthRange
                  ? invoiceYearMonthRange[invoiceYear]
                  : []
              }
              selectedItem={invoiceMonth}
              className="ml-[10px] w-[120px] text-dark-3 [&>div:first-child>button]:noto-medium-4 [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[40px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:pb-[13px] [&>div:first-child>button]:pl-[21px] [&>div:first-child>button]:pr-[15px] [&>div:first-child>button]:pt-[15px] [&>div:first-child>button]:ring-2 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&[data-headlessui-state=open]>div:first-child>button]:bg-light-2 [&[data-headlessui-state=open]>div:first-child>button]:ring-teal-dark"
              onSelected={(invoiceMonth) => {
                setInvoiceMonth(invoiceMonth);
              }}
            ></Dropdown>
            <SearchButton
              text="조회"
              className="ml-[10px] h-[40px] w-[80px] cursor-pointer"
              onClick={debouncedOnclickFetchInvoiceDataButton}
            ></SearchButton>
            {showFetchInoviceAlert && (
              <ForecastAlert
                message="정산서 조회에 실패했습니다."
                className="ml-[10px]"
              />
            )}
          </div>
          <div className="mt-[40px]">
            <p className="pop-semibold-5 leading-none"> 개별자원 </p>
            <IndividualResourceInvoiceTable
              data={individualResourceInvoiceData || []}
              className="mt-[20px]"
            ></IndividualResourceInvoiceTable>
          </div>
          <div className="mt-[40px]">
            <p className="pop-semibold-5 leading-none"> 집합자원 </p>
            <SetResourceInvoiceTable
              data={setResourceInvoiceData || []}
              className="mt-[20px]"
            ></SetResourceInvoiceTable>
          </div>
        </section>
      </div>
    </SideLayout>
  );
};

export default InvoicePage;
